import { useCallback, useMemo, useState } from "react";
import type { TablePaginationConfig } from "antd";

/**
 * The envelope the backend returns when a list request asks for a page.
 * See `backend/main/pagination.py` — pagination is opt-in, so an endpoint
 * called without `page`/`page_size` still returns a bare array.
 */
export type Paginated<T> = {
  count: number;
  num_pages: number;
  page: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export function isPaginated<T>(value: any): value is Paginated<T> {
  return (
    !!value &&
    typeof value === "object" &&
    Array.isArray(value.results) &&
    typeof value.count === "number"
  );
}

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = ["10", "25", "50", "100"];

/**
 * Server-side pagination state for a list page.
 *
 * Usage:
 *   const { page, pageSize, applyResponse, paginationProps, resetPage } = usePagination();
 *   useEffect(() => { ...fetch with page/pageSize...; setRows(applyResponse(res)); },
 *             [filters, page, pageSize]);
 *   <DynamicFilterForm onFilter={(v) => { setFilters(v); resetPage(); }} />
 *   <ManageableTable pagination={paginationProps} ... />
 *
 * Reset the page from the same event handler that changes the filters — both
 * state updates then batch into one render and one fetch. Resetting from a
 * `useEffect` on the filter value instead would fire two requests: the new
 * filters at the old page, then again at page 1.
 */
export function usePagination(defaultPageSize: number = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [total, setTotal] = useState<number>(0);

  /**
   * Unwraps a list response and records the total. Tolerates a plain array so
   * a page keeps working if its endpoint is ever called without paging.
   */
  const applyResponse = useCallback(<T,>(response: Paginated<T> | T[] | null | undefined): T[] => {
    if (isPaginated<T>(response)) {
      setTotal(response.count);
      return response.results;
    }
    const rows = Array.isArray(response) ? response : [];
    setTotal(rows.length);
    return rows;
  }, []);

  const resetPage = useCallback(() => setPage(1), []);

  const paginationProps: TablePaginationConfig = useMemo(
    () => ({
      current: page,
      pageSize,
      total,
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      showTotal: (count: number, range: [number, number]) =>
        `${range[0]}-${range[1]} / ${count}`,
      onChange: (nextPage: number, nextSize?: number) => {
        // Changing the page size re-anchors the window, so go back to page 1
        // rather than landing on a page that may no longer exist.
        if (nextSize && nextSize !== pageSize) {
          setPageSize(nextSize);
          setPage(1);
          return;
        }
        setPage(nextPage);
      },
    }),
    [page, pageSize, total]
  );

  return { page, pageSize, total, setPage, setPageSize, applyResponse, resetPage, paginationProps };
}
